import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Flex, Tooltip } from "antd";
import { ButtonProps } from "antd/lib";
import { t } from "i18next";
import { useContext, useMemo, useState } from "react";
import {
  NavigationType,
  useNavigate,
  useNavigationType,
} from "react-router-dom";
import Note from "../../../components/Note/Note";
import { ModalsContext } from "../../../context/modals.context";
import {
  mapDetailsToFormMode,
  renderStatusTag,
} from "../../../modules/productionCode/productionCode.functions";
import { useProductionCodeRequestDetails } from "../../../modules/productionCode/productionCode.queries";
import { ProductionCodeRequestStatus } from "../../../modules/productionCode/productionCode.types";
import { FormMode } from "../../../types";
import DownloadProductionCodeRequestFileButton from "../components/DownloadProductionCodesButton/DownloadProductionCodeRequestFileButton";
import style from "./ProductionCodeRequestPage.module.scss";
import ProductionCodeApprovalModal from "./components/ProductionCodeApprovalModal/ProductionCodeApprovalModal";
import ProductionCodeRequestForm from "./components/ProductionCodeRequestForm/ProductionCodeRequestForm";

interface ProductionCodeFormProps {
  formMode: FormMode;
  isMfin?: boolean;
}
const ProductionCodeRequestPage = ({
  formMode,
  isMfin,
}: ProductionCodeFormProps) => {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const [submitButtonProps, setSubmitButtonProps] = useState<
    Partial<ButtonProps>
  >({
    disabled: true,
  });
  const queryParams = new URLSearchParams(window.location.search);
  const productionCodeRequestId = Number(queryParams.get("id"));
  const { handleUpdateModals } = useContext(ModalsContext);

  const {
    data: productionCodeRequest,
    isLoading: isLoadingProductionCodeRequest,
  } = useProductionCodeRequestDetails({
    id: productionCodeRequestId,
    enabled: !!productionCodeRequestId,
  });

  const handleGoBack = () => {
    if (navigationType === NavigationType.Push) {
      navigate(-1);
    } else {
      navigate(
        isMfin
          ? "/mfin/produkcioniKodovi/lista"
          : "/obveznik/produkcioniKodovi/listaProdukcionihKodova"
      );
    }
  };

  const handleRequestApproval = (type: "decline" | "accept") => {
    handleUpdateModals({
      modalVisible: "productionCodeApprovalModal",
      zahtevId: productionCodeRequest?.zahtevId ?? productionCodeRequestId,
      type,
    });
  };

  const noteType = useMemo(() => {
    switch (productionCodeRequest?.statusId) {
      case ProductionCodeRequestStatus.ODBIJEN:
        return "error";
      case ProductionCodeRequestStatus.PRIHVACEN:
        return "success";
      case ProductionCodeRequestStatus.KREIRAN:
      default:
        return "default";
    }
  }, [productionCodeRequest?.statusId]);

  return (
    <>
      <ProductionCodeApprovalModal />
      <Flex
        flex={1}
        vertical
        style={{
          height: "100%",
        }}
      >
        <Flex
          flex={1}
          gap={20}
          style={{
            padding: "40px 40px 80px 40px",
          }}
        >
          <Flex className={style.stepAnimation} flex={1} gap={16} vertical>
            <Flex align="center">
              {renderStatusTag(
                productionCodeRequest?.statusId,
                productionCodeRequest?.status
              )}
              <span className={style.title}>
                {t("QR_CODE.ZAHTEV_ZA_GENERISANJE")}
              </span>
            </Flex>

            {productionCodeRequest?.note ? (
              <Note note={productionCodeRequest?.note} type={noteType} />
            ) : (
              <></>
            )}
            <ProductionCodeRequestForm
              formMode={formMode}
              initialValues={mapDetailsToFormMode(productionCodeRequest)}
              initialValuesDetails={productionCodeRequest}
              isLoading={isLoadingProductionCodeRequest}
              handleSetSubmitButtonProps={(prop: Partial<ButtonProps>) =>
                setSubmitButtonProps((prevProps) => ({ ...prevProps, ...prop }))
              }
              useHelperFormData={!isMfin}
            />
          </Flex>
        </Flex>
        <Flex align="center" gap={10} className={style.footer}>
          <Flex flex={1}>
            <Button
              onClick={handleGoBack}
              icon={<ArrowLeftOutlined />}
              className={style.fontSize}
            >
              {t("AKCIZNE_MARKICE.NAZAD")}
            </Button>
          </Flex>

          <Flex flex={1} gap={10} justify="end">
            {formMode !== FormMode.DETAILS ? (
              <Tooltip
                title={
                  true ? t("COMMON.POPUNITE_SVA_OBAVEZNA_POLJA") : undefined
                }
              >
                <Button
                  htmlType="submit"
                  type="primary"
                  form="productionCodeForm"
                  className={style.fontSize}
                  {...submitButtonProps}
                >
                  {t("SKLADISTA.POSALJI_ZAHTEV")}
                </Button>
              </Tooltip>
            ) : (
              <>
                {isMfin &&
                productionCodeRequest?.statusId ===
                  ProductionCodeRequestStatus.KREIRAN ? (
                  <>
                    <Button
                      className={style.fontSize}
                      onClick={() => handleRequestApproval("decline")}
                      danger
                      ghost
                    >
                      {t("COMMON.ODBIJ")}
                    </Button>
                    <Button
                      className={style.fontSize}
                      onClick={() => handleRequestApproval("accept")}
                    >
                      {t("COMMON.PRIHVATI")}
                    </Button>
                  </>
                ) : (
                  <></>
                )}
                {productionCodeRequest?.statusId ===
                ProductionCodeRequestStatus.PRIHVACEN ? (
                  <DownloadProductionCodeRequestFileButton
                    id={productionCodeRequestId}
                    type="codes"
                    buttonProps={{ type: "primary" }}
                    docName={productionCodeRequest?.brojZahteva}
                    sifraRobneMarke={
                      productionCodeRequest?.sifraRobneMarke ?? null
                    }
                  />
                ) : (
                  <></>
                )}
                {productionCodeRequest?.statusId ===
                ProductionCodeRequestStatus.KREIRAN ? (
                  <DownloadProductionCodeRequestFileButton
                    id={productionCodeRequestId}
                    type="proformaInvoice"
                    buttonProps={{ type: "primary" }}
                    docName={productionCodeRequest?.brojZahteva}
                    sifraRobneMarke={
                      productionCodeRequest?.sifraRobneMarke ?? null
                    }
                  />
                ) : (
                  <></>
                )}
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default ProductionCodeRequestPage;
